export type LatLng = [number, number]

const getCurrentPosition = () => new Promise<GeolocationPosition>((resolve, reject) => {
  window.navigator.geolocation.getCurrentPosition(resolve, reject)
})

/** Asks the browser for its location; null when denied or unavailable */
export const getBrowserGeolocation = async (): Promise<LatLng | null> => {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const { state } = await window.navigator.permissions.query({ name: 'geolocation' })
    if (state === 'denied') {
      return null
    }
    const geo = await getCurrentPosition()
    return [geo.coords.latitude, geo.coords.longitude]
  } catch {
    return null
  }
}

/** Great-circle distance in metres */
export const getGpsDistance = (lat: number, lng: number, lat0: number, lng0: number): number => {
  const R = 6372795
  const latR = lat * Math.PI / 180
  const lat0R = lat0 * Math.PI / 180
  const delta = (lng0 - lng) * Math.PI / 180
  const cl1 = Math.cos(latR)
  const cl2 = Math.cos(lat0R)
  const sl1 = Math.sin(latR)
  const sl2 = Math.sin(lat0R)
  const cdelta = Math.cos(delta)
  const sdelta = Math.sin(delta)
  const y = Math.sqrt(Math.pow(cl2 * sdelta, 2) + Math.pow(cl1 * sl2 - sl1 * cl2 * cdelta, 2))
  const x = sl1 * sl2 + cl1 * cl2 * cdelta
  return Math.atan2(y, x) * R
}

/** Parses a CMS "lat,lng" coordinate string */
export const parseCoords = (coords: string | null | undefined): LatLng | null => {
  if (!coords) {
    return null
  }
  const [lat, lng] = coords.split(',').map((part) => parseFloat(part.trim()))
  if (Number.isNaN(lat) || Number.isNaN(lng) || lat === undefined || lng === undefined) {
    return null
  }
  return [lat, lng]
}
